import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaUser, FaShoppingCart, FaBars, FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaShieldAlt } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useCatalog } from '../../context/CatalogContext';
import { useAdmin } from '../../context/AdminContext';
import { useLanguage } from '../../context/LanguageContext';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { getCartCount } = useCart();
  const { products, categories, brands } = useCatalog();
  const { isAdmin } = useAdmin();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const saved = localStorage.getItem('searchHistory');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSearchHistory(parsed);
        }
      }
    } catch {
      localStorage.removeItem('searchHistory');
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const suggestions = useMemo(() => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return products
      .filter(p => p.name.toLowerCase().includes(query) || p.brand.toLowerCase().includes(query))
      .slice(0, 6);
  }, [debouncedQuery, products]);

  const combinedItems = useMemo(() => {
    const suggestionItems = suggestions.map(item => ({
      id: `suggestion-${item.id}`,
      label: item.name,
      meta: item.brand,
      type: 'suggestion'
    }));
    const historyItems = searchHistory.map(item => ({
      id: `history-${item}`,
      label: item,
      type: 'history'
    }));
    return [...suggestionItems, ...historyItems].slice(0, 10);
  }, [suggestions, searchHistory]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [combinedItems.length, isSearchOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const normalized = searchQuery.trim();
      const nextHistory = [normalized, ...searchHistory.filter(q => q !== normalized)].slice(0, 8);
      setSearchHistory(nextHistory);
      localStorage.setItem('searchHistory', JSON.stringify(nextHistory));
      setIsSearchOpen(false);
      navigate(`/shop?search=${encodeURIComponent(normalized)}`);
    }
  };

  const handleSuggestionClick = (value) => {
    setSearchQuery(value);
    const nextHistory = [value, ...searchHistory.filter(q => q !== value)].slice(0, 8);
    setSearchHistory(nextHistory);
    localStorage.setItem('searchHistory', JSON.stringify(nextHistory));
    setIsSearchOpen(false);
    navigate(`/shop?search=${encodeURIComponent(value)}`);
  };

  const handleKeyDown = (e) => {
    if (!isSearchOpen || combinedItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % combinedItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + combinedItems.length) % combinedItems.length);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(combinedItems[activeIndex].label);
    } else if (e.key === 'Escape') {
      setIsSearchOpen(false);
    }
  };

  return (
    <header className="header">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="top-bar-left">
              <span className="top-bar-item">
                <FaPhone /> +234-8054459668, +234-9012222220
              </span>
              <span className="top-bar-item">
                <FaEnvelope /> info@haksaltech.com
              </span>
            </div>
            <div className="top-bar-right">
              <Link to="/track-order" className="top-bar-link">Track Your Order</Link>
              <Link to="/store-locator" className="top-bar-link">
                <FaMapMarkerAlt /> Store Locator
              </Link>
              <a href="https://wa.me/2348054459668" className="top-bar-link whatsapp">
                <FaWhatsapp /> Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="main-header">
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <Link to="/" className="logo">
              <img src="/logo.png" alt="Haksaltech Logo" />
            </Link>

            {/* Search Bar */}
            <form className="search-bar" onSubmit={handleSearch}>
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchOpen(true)}
                onBlur={() => setTimeout(() => setIsSearchOpen(false), 150)}
                onKeyDown={handleKeyDown}
                aria-label="Search products"
                aria-autocomplete="list"
                aria-expanded={isSearchOpen}
                aria-controls="search-suggestions"
                role="combobox"
              />
              <button type="submit" aria-label="Submit search">
                <FaSearch />
              </button>
              {isSearchOpen && (suggestions.length > 0 || searchHistory.length > 0) && (
                <div className="search-suggestions" id="search-suggestions" role="listbox">
                  {suggestions.length > 0 && (
                    <div className="suggestion-title">Suggestions</div>
                  )}
                  {searchHistory.length > 0 && (
                    <div className="suggestion-title">Recent Searches</div>
                  )}
                  {combinedItems.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`suggestion-item ${item.type === 'history' ? 'history' : ''}`}
                      onMouseDown={() => handleSuggestionClick(item.label)}
                      role="option"
                      aria-selected={index === activeIndex}
                    >
                      {item.label}
                      {item.meta && <span className="suggestion-brand">{item.meta}</span>}
                    </button>
                  ))}
                </div>
              )}
            </form>

            {/* Header Actions */}
            <div className="header-actions">
              <Link to="/my-account" className="header-action">
                <FaUser />
                <span>{t('myAccount')}</span>
              </Link>
              {isAdmin && (
                <Link to="/admin" className="header-action">
                  <FaShieldAlt />
                  <span>Admin</span>
                </Link>
              )}
              <Link to="/cart" className="header-action cart-icon">
                <FaShoppingCart />
                <span>{t('cart')}</span>
                {getCartCount() > 0 && (
                  <span className="cart-count">{getCartCount()}</span>
                )}
              </Link>
              <select
                className="language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                aria-label="Select language"
              >
                <option value="en">EN</option>
                <option value="es">ES</option>
              </select>
            </div>

            {/* Mobile Menu Toggle */}
            <button className="mobile-menu-toggle" onClick={toggleMenu} aria-label="Toggle menu">
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`main-nav ${isMenuOpen ? 'open' : ''}`}>
        <div className="container">
          <ul className="nav-list">
            <li className="nav-item has-dropdown">
              <Link to="/shop">Shop</Link>
              <div className="mega-dropdown">
                <div className="mega-dropdown-content">
                  <div className="mega-column">
                    <h4>Categories</h4>
                    <ul>
                      {categories.map(cat => (
                        <li key={cat.id}>
                          <Link to={`/category/${cat.slug}`}>{cat.name}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mega-column">
                    <h4>Popular Brands</h4>
                    <ul>
                      {brands.slice(0, 6).map(brand => (
                        <li key={brand.id}>
                          <Link to={`/shop?brand=${brand.slug}`}>{brand.name}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>
            {categories.map(cat => (
              <li className="nav-item" key={cat.id}>
                <Link to={`/category/${cat.slug}`}>{cat.name}</Link>
              </li>
            ))}
          </ul>

          {/* Brand Links */}
          <div className="brand-links">
            {brands.slice(0, 6).map(brand => (
              <Link to={`/shop?brand=${brand.slug}`} key={brand.id} className="brand-link">
                {brand.name}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
